import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { compactSelectClass } from '../styles';
import { fetchCreditBatches } from './api';
import type { CreditBatchRow, OrdersMeta } from './types';

interface Props {
  meta: OrdersMeta;
}

const CreditBatchesPanel: React.FC<Props> = ({ meta }) => {
  const [rows, setRows] = useState<CreditBatchRow[]>([]);
  const [orgFilter, setOrgFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchCreditBatches({
          org: orgFilter || undefined,
          status: statusFilter || undefined,
        });
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load credit batches');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgFilter, statusFilter]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <select
          className={compactSelectClass}
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value ? Number(e.target.value) : '')}
          aria-label="Filter by organization"
        >
          <option value="">All orgs</option>
          {meta.orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <select
          className={compactSelectClass}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {meta.batch_statuses.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      {error && <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[12px] text-[#626262]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Organization</th>
                <th className="text-right px-3 py-2 font-medium">Granted</th>
                <th className="text-right px-3 py-2 font-medium">Remaining</th>
                <th className="text-left px-3 py-2 font-medium">Currency</th>
                <th className="text-left px-3 py-2 font-medium">Granted at</th>
                <th className="text-left px-3 py-2 font-medium">Expires at</th>
                <th className="text-left px-3 py-2 font-medium">Note</th>
                <th className="text-left px-3 py-2 font-medium">Approved by</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-[#F0F0F0]">
                  <td className="px-3 py-2">{row.org_name || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.amount_granted}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.amount_remaining}</td>
                  <td className="px-3 py-2">{row.currency_code || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.granted_at ? new Date(row.granted_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.expires_at ? new Date(row.expires_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 max-w-[280px] truncate" title={row.note}>
                    {row.note || '—'}
                  </td>
                  <td className="px-3 py-2">{row.approved_by_email || '—'}</td>
                  <td className="px-3 py-2 capitalize">{row.status}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-[#8A8A8A]">
                    No credit batches
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CreditBatchesPanel;
